/**
 * Discography metadata — the 7 studio albums documented in the legacy
 * WP site (70 tracks). PARTIAL set: Russ has played on additional
 * recordings across his career that aren't represented here (no source
 * data recovered). Studio entries sourced from the legacy WP `album`
 * custom post type (postmeta tables `alb_release_date` for the artist
 * + `alb_tracklist` for the track order).
 *
 * A 6-track Moonshine Society "Live: 8/11/2020 Virtual Concert" entry
 * was REMOVED 2026-08-11 at Russ's request (call outcome 2; see
 * docs/russ-call-2026-08-12.md). Its 35 MB of audio was deleted from
 * public/audio/ in the same commit — recover from git history if it is
 * ever wanted back. The `kind: 'live'` variant on DiscographyAlbum is
 * retained for future live entries; no album currently uses it.
 *
 * Audio files live at /audio/<slug>/<filename> after running
 * `npx tsx scripts/copy-discography-audio.mts` one-time from the WP
 * backup.
 *
 * Russ played bass on every entry. No release-year metadata for the
 * studio albums in the WP source; can be supplemented by hand later
 * if Russ has the info.
 */

export interface DiscographyTrack {
  /** 1-based position within the album (preserved from WP source order). */
  readonly number: number;
  /** Track display title. WP attachment post_title was used as the
   * primary source; falls back to a humanized form of the filename
   * (relevant for the George Huely album whose track titles were
   * never filled in). */
  readonly title: string;
  /** Source MP3 filename — the file at /audio/<album-slug>/<filename> */
  readonly filename: string;
}

export interface DiscographyAlbum {
  /** URL slug for in-page anchors and audio dir name. */
  readonly slug: string;
  /** Album title as released. */
  readonly title: string;
  /** Bandleader / primary artist. Russ played bass on all of these. */
  readonly artist: string;
  /** Studio (default) or live. Live entries get a different visual
   *  treatment + label on the page. */
  readonly kind?: 'studio' | 'live';
  /** Optional WP-uploads sub-path (e.g. '2023/02') — defaults to
   *  '2020/04' (where the original 7 studio albums lived). Used by
   *  scripts/copy-discography-audio.mts to find source MP3s. */
  readonly sourceSubdir?: string;
  /** Optional external link to the full performance video (e.g. a
   *  YouTube watch URL for a live concert). Rendered as a "Watch on
   *  YouTube" link near the album header. */
  readonly videoUrl?: string;
  /** Optional release year (gregorian). Sourced from Discogs / artist
   *  pages where available; some albums (Kodac Harrison self-titled,
   *  George Huely, Stephanie Pettis & Rio) need Russ to confirm. */
  readonly releaseYear?: number;
  /** Optional cover art. WebP filename under
   *  /images/discography/<filename>. width/height are the source
   *  pixel dimensions (used by NgOptimizedImage to reserve layout
   *  space; CSS resizes to the displayed thumbnail width). */
  readonly coverImage?: {
    readonly filename: string;
    readonly width: number;
    readonly height: number;
  };
  /** Tracks in album order. */
  readonly tracks: readonly DiscographyTrack[];
}

export const DISCOGRAPHY: readonly DiscographyAlbum[] = [
  {
    // The session group on the WP source was labelled "League Of
    // Decency"; the actual released album is "Jump Yer Bones" by
    // Mondo Heptet (CMG / Music Mind Corp, 2000). "Manic Depression"
    // (track 12) is in our source set but isn't on the canonical
    // Discogs tracklist — possibly a bonus / outtake / alternate
    // pressing. Flagged for Russ to clarify.
    slug: 'mondo-heptet-jump-yer-bones',
    title: 'Jump Yer Bones',
    artist: 'Mondo Heptet (League Of Decency session)',
    releaseYear: 2000,
    coverImage: {
      filename: 'mondo-heptet-jump-yer-bones.webp',
      width: 600,
      height: 546,
    },
    tracks: [
      { number: 1, title: 'Abdulla', filename: '01-Abdulla.mp3' },
      {
        number: 2,
        title: "Jumpin' At The Jubilee",
        filename: '02-Jumpin-At-The-Jubilee.mp3',
      },
      {
        number: 3,
        title: 'Bible Belt Bopper',
        filename: '03-Bible-Belt-Bopper.mp3',
      },
      { number: 4, title: 'Li', filename: '04-Li.mp3' },
      {
        number: 5,
        title: "Swingin' For Crack",
        filename: '05-Swingin-For-Crack.mp3',
      },
      { number: 6, title: 'The Poon Tango', filename: '06-The-Poon-Tango.mp3' },
      {
        number: 7,
        title: 'I Always Land On My Feet',
        filename: '07-I-Always-Land-On-My-Feet.mp3',
      },
      { number: 8, title: 'Wombat Baby', filename: '08-Wombat-Baby.mp3' },
      { number: 9, title: "Jivin' Me", filename: '09-Jivin-Me.mp3' },
      { number: 10, title: 'Dixie Bop', filename: '10-Dixie-Bop.mp3' },
      { number: 11, title: 'In The Jungle', filename: '11-In-The-Jungle.mp3' },
      {
        number: 12,
        title: 'Manic Depression',
        filename: 'Manic-Depression.mp3',
      },
    ],
  },
  {
    slug: 'kodac-harrison',
    title: 'Kodac Harrison',
    artist: 'Kodac Harrison',
    tracks: [
      {
        number: 1,
        title: "That's How Strong My Love",
        filename: 'Thats-How-Strong-My-Love.mp3',
      },
      { number: 2, title: 'Soul Chains', filename: 'Soul-Chains.mp3' },
      {
        number: 3,
        title: 'Must Be Summertime',
        filename: 'Must-Be-Summertime.mp3',
      },
      { number: 4, title: 'Loving You', filename: 'Loving-You.mp3' },
      {
        number: 5,
        title: 'Love Turned On The Light',
        filename: 'Love-Turned-On-The-Light.mp3',
      },
      { number: 6, title: 'Lazy River', filename: 'Lazy-River.mp3' },
      { number: 7, title: 'Lazy Love', filename: 'Lazy-Love.mp3' },
      { number: 8, title: 'Killing Machine', filename: 'Killing-Machine.mp3' },
      { number: 9, title: 'Hotel 17', filename: 'Hotel-17.mp3' },
      {
        number: 10,
        title: 'Got Laid Last Night',
        filename: 'Got-Laid-Last-Night.mp3',
      },
      {
        number: 11,
        title: 'Decadent Delusions',
        filename: 'Decadent-Delusions.mp3',
      },
    ],
  },
  {
    // Francine Reed — "I Got A Right! ...To Some Of My Best"
    // (CMG Records CMG-1010, 2001). Our 11 tracks correspond to CD
    // positions 1-11; the album also has a track #12 ("Please Send Me
    // Someone To Love") and a bonus #13 ("Out Of The Clear Blue Sky")
    // that the WP source set didn't include.
    slug: 'ive-got-a-right',
    title: 'I Got A Right! ...To Some Of My Best',
    artist: 'Francine Reed',
    releaseYear: 2001,
    coverImage: {
      filename: 'francine-reed-i-got-a-right.webp',
      width: 600,
      height: 600,
    },
    tracks: [
      {
        number: 1,
        title: 'Been There, Done That',
        filename: 'Been-There-Done-That.mp3',
      },
      { number: 2, title: 'The Night Life', filename: 'The-Night-Life.mp3' },
      {
        number: 3,
        title: "One Monkey Don't Stop No Show",
        filename: 'One-Monkey-Dont-Stop-No-Show.mp3',
      },
      { number: 4, title: 'Leaving Town', filename: 'Leaving-Town.mp3' },
      { number: 5, title: 'Crack The Bone', filename: 'Crack-The-Bone.mp3' },
      { number: 6, title: 'Trouble In Mind', filename: 'Trouble-In-Mind.mp3' },
      {
        number: 7,
        title: 'I Want You To Love Me',
        filename: 'I-Want-You-To-Love-Me.mp3',
      },
      {
        number: 8,
        title: 'Stop Me! Before I Love Again',
        filename: 'Stop-Me-Before-I-Love-Again.mp3',
      },
      {
        number: 9,
        title: "Wild Women Don't Get The Blues",
        filename: 'Wild-Women-Dont-Get-The-Blues.mp3',
      },
      {
        number: 10,
        title: "Let's Work Together",
        filename: 'Lets-Work-Together.mp3',
      },
      {
        number: 11,
        title: 'What Is This Light?',
        filename: 'What-Is-That-Light_.mp3',
      },
    ],
  },
  {
    // Tom Grose & The Varsity with Uncle Daddy — "Unannounced And
    // Uninvited" (Tom Grose Music, 1998). Full CD has 12 tracks;
    // our 3 are CD positions 2, 8, 11 (Russ played bass on these).
    // Displayed in CD order with sequential numbering.
    slug: 'unannounced-and-uninvited',
    title: 'Unannounced And Uninvited',
    artist: 'Tom Grose & The Varsity',
    releaseYear: 1998,
    coverImage: {
      filename: 'tom-grose-unannounced-and-uninvited.webp',
      width: 300,
      height: 300,
    },
    tracks: [
      { number: 1, title: 'Say My Name', filename: 'Say-My-Name.mp3' },
      {
        number: 2,
        title: 'You Got It Wrong',
        filename: 'You-Got-It-Wrong.mp3',
      },
      {
        number: 3,
        title: "It's Got To Be Me",
        filename: 'Its-Got-To-Be-Me.mp3',
      },
    ],
  },
  {
    // Henry Parrilla — "Widespread Hispanic" (CMG Productions, 2000).
    // All 11 tracks present, reordered to match the Discogs CD order
    // and with title casing fixed (C.C. Rider, C'est La Vie (Never
    // Can Tell), Inventario De Amor).
    slug: 'wide-spread-hispanic',
    title: 'Widespread Hispanic',
    artist: 'Henry Parrilla',
    releaseYear: 2000,
    coverImage: {
      filename: 'henry-parrilla-widespread-hispanic.webp',
      width: 448,
      height: 442,
    },
    tracks: [
      {
        number: 1,
        title: "Lover's Question",
        filename: 'Lovers-Question.mp3',
      },
      { number: 2, title: "Them That's Got", filename: 'Them-Thats-Got.mp3' },
      { number: 3, title: 'Lipstick Traces', filename: 'Lipstick-Traces.mp3' },
      {
        number: 4,
        title: 'Inventario De Amor',
        filename: 'Inventario-de-Amore.mp3',
      },
      {
        number: 5,
        title: "C'est La Vie (Never Can Tell)",
        filename: 'Cest-LA-Vie.mp3',
      },
      {
        number: 6,
        title: 'I Want To Love You',
        filename: 'I-Want-To-Love-You.mp3',
      },
      {
        number: 7,
        title: 'You Are My Sunshine',
        filename: 'You-Are-My-Sunshine.mp3',
      },
      { number: 8, title: 'Just Because', filename: 'Just-Because.mp3' },
      { number: 9, title: 'C.C. Rider', filename: 'CC-Rider.mp3' },
      {
        number: 10,
        title: "It's All Behind Me Now",
        filename: 'Its-All-Behind-Me-Now.mp3',
      },
      {
        number: 11,
        title: "I've Got It Bad And That Ain't Good",
        filename: 'Ive-Got-It-Bad-And-That-Aint-Good.mp3',
      },
    ],
  },
  {
    slug: 'stephanie-pettis-and-rio',
    title: 'Stephanie Pettis And Rio',
    artist: 'Stephanie Pettis',
    tracks: [
      { number: 1, title: 'Soniando', filename: 'Soniando.mp3' },
      { number: 2, title: 'We Were Dancing', filename: 'We-Were-Dancing.mp3' },
      { number: 3, title: 'La Costa', filename: 'La-Costa.mp3' },
      { number: 4, title: 'Cravo E Canela', filename: 'Cravo-E-Canela.mp3' },
      {
        number: 5,
        title: "Ain't No Sunshine",
        filename: 'Aint-No-Sunshine.mp3',
      },
      { number: 6, title: 'Hang On Sloopy', filename: 'Hang-On-Sloopy.mp3' },
      { number: 7, title: 'Hot!Hot!Hot!', filename: 'HotHotHot.mp3' },
      { number: 8, title: 'La Bamba', filename: 'La-Bamba.mp3' },
      {
        number: 9,
        title: "I'll Take You There",
        filename: 'Ill-Take-You-There.mp3',
      },
      { number: 10, title: 'Happy Together', filename: 'Happy-Together.mp3' },
      {
        number: 11,
        title: "With You I'm Born Again",
        filename: 'With-You-Im-Born-Again.mp3',
      },
    ],
  },
  {
    slug: 'george-huely',
    title: 'George Huely',
    artist: 'George Huely',
    tracks: [
      { number: 1, title: 'Track 01', filename: 'Track-01.mp3' },
      { number: 2, title: 'Track 02', filename: 'Track-02.mp3' },
      { number: 3, title: 'Track 03', filename: 'Track-03.mp3' },
      { number: 4, title: 'Track 04', filename: 'Track-04.mp3' },
      { number: 5, title: 'Track 05', filename: 'Track-05.mp3' },
      { number: 6, title: 'Track 06', filename: 'Track-06.mp3' },
      { number: 7, title: 'Track 07', filename: 'Track-07.mp3' },
      { number: 8, title: 'Track 08', filename: 'Track-08.mp3' },
      { number: 9, title: 'Track 09', filename: 'Track-09.mp3' },
      { number: 10, title: 'Track 10', filename: 'Track-10.mp3' },
      { number: 11, title: 'Track 11', filename: 'Track-11.mp3' },
    ],
  },
];
